import { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

export const DEFAULT_DATE_RANGE = 'today';

const DATE_RANGE_OPTIONS = [
  { value: 'all_time', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'last_year', label: 'Last Year' },
];

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const pad2 = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const parseYmd = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};
const sameDay = (a, b) => Boolean(a && b) && ymd(a) === ymd(b);
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const monthStartMinus = (ref, months) => new Date(ref.getFullYear(), ref.getMonth() - months, 1);

const presetRange = (range, customStart, customEnd) => {
  const end = startOfDay(new Date());
  if (range === 'custom') return { start: parseYmd(customStart), end: parseYmd(customEnd) };
  if (range === 'all_time') return { start: null, end: null };
  if (range === 'today') return { start: end, end };
  const starts = {
    this_month: new Date(end.getFullYear(), end.getMonth(), 1),
    last_3_months: monthStartMinus(end, 3),
    last_6_months: monthStartMinus(end, 6),
    last_year: monthStartMinus(end, 12),
  };
  const start = starts[range];
  return start ? { start, end } : { start: null, end: null };
};

const DateRangeFilter = ({
  value = DEFAULT_DATE_RANGE,
  onChange,
  customStart = '',
  customEnd = '',
  onCustomStartChange,
  onCustomEndChange,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => new Date());
  const [anchor, setAnchor] = useState(null);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const [panelPos, setPanelPos] = useState({ top: 0, right: 8 });
  const selected = presetRange(value, customStart, customEnd);

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPanelPos({ top: rect.bottom + 6, right: Math.max(8, window.innerWidth - rect.right) });
    };
    place();
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  const days = useMemo(() => {
    const first = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    const count = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
    const cells = Array.from({ length: first.getDay() }, () => null);
    for (let day = 1; day <= count; day += 1) {
      cells.push(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day));
    }
    return cells;
  }, [viewMonth]);

  const openPanel = () => {
    const range = presetRange(value, customStart, customEnd);
    setViewMonth(range.start || new Date());
    setAnchor(null);
    setOpen((current) => !current);
  };

  const choosePreset = (next) => {
    onChange?.(next);
    const range = presetRange(next, customStart, customEnd);
    if (range.start) setViewMonth(range.start);
    setAnchor(null);
    setOpen(false);
  };

  const chooseDay = (day) => {
    if (!anchor) {
      setAnchor(day);
      return;
    }
    const start = anchor <= day ? anchor : day;
    const end = anchor <= day ? day : anchor;
    onCustomStartChange?.(ymd(start));
    onCustomEndChange?.(ymd(end));
    onChange?.('custom');
    setAnchor(null);
    setOpen(false);
  };

  const rangeStart = anchor || selected.start;
  const rangeEnd = anchor ? null : selected.end;
  const inRange = (day) => {
    if (!rangeStart || !rangeEnd) return false;
    const time = day.getTime();
    return time >= rangeStart.getTime() && time <= rangeEnd.getTime();
  };

  const monthLabel = viewMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  return (
    <div ref={rootRef} className={`relative w-fit shrink-0 justify-self-end ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={openPanel}
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100"
        aria-label="Date range"
        aria-expanded={open}
        title="Date range"
      >
        <Calendar className="w-4 h-4" />
      </button>
      {open && (
        <div
          className="fixed z-[80] w-[280px] max-w-[calc(100vw-1rem)] rounded-xl border border-gray-200 bg-white p-3 shadow-lg"
          style={{ top: panelPos.top, right: panelPos.right }}
        >
          <div className="mb-2 flex flex-wrap gap-1">
            {DATE_RANGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => choosePreset(option.value)}
                className={`rounded-md px-1.5 py-1 text-[11px] leading-none ${
                  value === option.value ? 'bg-blue-600 font-semibold text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              className="rounded-md p-1 text-gray-500 hover:bg-gray-100"
              aria-label="Previous month"
              onClick={() => setViewMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <p className="text-sm font-semibold text-gray-800">{monthLabel}</p>
            <button
              type="button"
              className="rounded-md p-1 text-gray-500 hover:bg-gray-100"
              aria-label="Next month"
              onClick={() => setViewMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-7 text-center text-[11px] text-gray-400">
            {WEEKDAYS.map((label) => (
              <div key={label} className="py-1">{label}</div>
            ))}
            {days.map((day, index) => {
              if (!day) return <div key={`empty-${index}`} />;
              const selectedDay = sameDay(day, rangeStart) || sameDay(day, rangeEnd);
              const covered = inRange(day);
              const today = sameDay(day, startOfDay(new Date()));
              return (
                <button
                  key={ymd(day)}
                  type="button"
                  onClick={() => chooseDay(day)}
                  className={`mx-auto flex h-8 w-8 items-center justify-center rounded-full text-xs ${
                    selectedDay
                      ? 'bg-blue-600 font-semibold text-white'
                      : covered
                        ? 'bg-blue-50 text-blue-700'
                        : today
                          ? 'font-semibold text-blue-600'
                          : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export function buildDateParams(dateRange, customStart, customEnd) {
  const params = {};
  if (dateRange && dateRange !== 'all_time') {
    params.date_range = dateRange;
    if (dateRange === 'custom') {
      if (customStart) params.from_date = customStart;
      if (customEnd) params.to_date = customEnd;
    }
  }
  return params;
}

/** from_date/to_date matching dashboard date_range (company settlement has no date_range arg). */
export function buildAbsoluteDateParams(dateRange, customStart, customEnd) {
  if (!dateRange || dateRange === 'all_time') return {};
  if (dateRange === 'custom') {
    const params = {};
    if (customStart) params.from_date = customStart;
    if (customEnd) params.to_date = customEnd;
    return params;
  }
  const today = startOfDay(new Date());
  if (dateRange === 'today') {
    const iso = ymd(today);
    return { from_date: iso, to_date: iso };
  }
  const starts = {
    this_month: new Date(today.getFullYear(), today.getMonth(), 1),
    last_3_months: monthStartMinus(today, 3),
    last_6_months: monthStartMinus(today, 6),
    last_year: monthStartMinus(today, 12),
  };
  const start = starts[dateRange];
  if (!start) return {};
  return { from_date: ymd(start), to_date: ymd(today) };
}

/** Open the role's settlement work page with the same date window and hub filter as the dashboard. */
export function companySettlementApprovalsPath(dateRange, customStart, customEnd, hubIds, role) {
  const params = new URLSearchParams();
  const dates = buildAbsoluteDateParams(dateRange, customStart, customEnd);
  if (dates.from_date) params.set('from_date', dates.from_date);
  if (dates.to_date) params.set('to_date', dates.to_date);
  const hubs = (hubIds || []).map(String).filter(Boolean);
  if (hubs.length) params.set('hub_ids', hubs.join(','));
  const qs = params.toString();
  const base = role === 'Hub Admin' ? '/company-settlements' : '/company-settlement-approvals';
  return qs ? `${base}?${qs}` : base;
}

export default DateRangeFilter;
